import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  fetchRegions,
  fetchPlants,
  fetchMaterialTypes,
  fetchMrpControllers,
  fetchMaterials,
} from "../../../services/Alerts/AlertPreferencesService";
import {
  fetchSavedFilters,
  upsertSavedFilter,
} from "../../../services/GlobalServiceFilters";
import { fetchFilterOptions as fetchInboundFilterOptions } from "../../../services/InboundShipments/inboundServices";
import { fetchFilterOptions as fetchOutboundFilterOptions } from "../../../services/OutboundShipments/outboundServices";

const SIDE_DRAWER_WIDTH = 56;
const HEADER_HEIGHT = 84;
const drawerWidth = 260;

const DEFAULT_SHIPMENT_STATE = {
  inbound: true,
  outbound: true,
  loads: true,
};

const buildShipmentState = (source) => ({
  inbound: source?.inbound ?? DEFAULT_SHIPMENT_STATE.inbound,
  outbound: source?.outbound ?? DEFAULT_SHIPMENT_STATE.outbound,
  loads: source?.loads ?? DEFAULT_SHIPMENT_STATE.loads,
});

const CATEGORY_CODE = "INVENTORY";
const MATERIAL_PAGE_SIZE = 50;

const UOM_OPTIONS = ["EA", "CS", "PL", "KG", "LB", "L", "GAL"];

const TXN_LOADS_OPTIONS = ["Load 001", "Load 002", "Load 003"];
const TXN_SHIP_TO_OPTIONS = ["Ship To 001", "Ship To 002"];
const TXN_SHIP_TO_NAME_OPTIONS = ["Customer DC 1", "Customer DC 2"];
const TXN_SHIP_MODE_OPTIONS = ["Road", "Rail", "Ocean", "Air"];


const mergeUnique = (a = [], b = []) =>
  Array.from(new Set([...(a || []), ...(b || [])]));

const normalizeOptions = (options = []) =>
  (options || [])
    .map((opt) => {
      if (opt == null) return "";
      if (typeof opt === "string") return opt;
      if (typeof opt === "number") return String(opt);
      return opt.label ?? opt.name ?? opt.value ?? "";
    })
    .filter((opt) => opt && typeof opt === "string");

const getOptionLabelSafe = (option) => {
  if (option == null) return "";
  if (typeof option === "string") return option;
  if (typeof option === "number") return String(option);
  return option.label ?? option.name ?? option.value ?? "";
};
const safeOptions = (arr = []) =>
  (Array.isArray(arr) ? arr : []).filter((opt) => opt != null);

const filterOptionsSafe = (options, state) => {
  const input = (state?.inputValue ?? "").toLowerCase();
  return safeOptions(options).filter((opt) =>
    getOptionLabelSafe(opt).toLowerCase().includes(input)
  );
};
const isOptionEqualToValueSafe = (option, value) =>
  getOptionLabelSafe(option) === getOptionLabelSafe(value);

const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const GlobalFilter = ({ open, onClose, shipmentTypes, setShipmentTypes, title = "GLOBAL FILTERS",
    tooltip = "The filters will be applied across all pages",

}) => {
  const { email: reduxEmail } = useSelector((state) => state.user || {});
  const userEmail = reduxEmail || "";

  const [searchItem, setSearchItem] = useState("");
  const [searchSite, setSearchSite] = useState("");
  const [shipFrom, setShipFrom] = useState("");
  const [shipTo, setShipTo] = useState("");
  const [carrier, setCarrier] = useState("");
  const [shipMode, setShipMode] = useState("");
  const [filterName, setFilterName] = useState("");
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [draftShipmentTypes, setDraftShipmentTypes] = useState(() =>
    buildShipmentState(shipmentTypes)
  );

  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedMaterialTypes, setSelectedMaterialTypes] = useState([]);
  const [selectedMrpControllers, setSelectedMrpControllers] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedUoms, setSelectedUoms] = useState([]);

  const [regions, setRegions] = useState([]);
  const [plants, setPlants] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [mrpControllers, setMrpControllers] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingPlants, setLoadingPlants] = useState(false);
  const [loadingMaterialTypes, setLoadingMaterialTypes] = useState(false);
  const [loadingMrpControllers, setLoadingMrpControllers] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  const [materialSearchText, setMaterialSearchText] = useState("");
  const debouncedMaterialSearch = useDebounce(materialSearchText);

  const [showTransactional, setShowTransactional] = useState(false);

  const [txnLoads, setTxnLoads] = useState([]);
  const [txnSupplierNumber, setTxnSupplierNumber] = useState([]);
  const [txnSupplierName, setTxnSupplierName] = useState([]);
  const [txnCustomerNumber, setTxnCustomerNumber] = useState([]);
  const [txnCustomerName, setTxnCustomerName] = useState([]);
  const [txnCarrierNumber, setTxnCarrierNumber] = useState([]);
  const [txnCarrierName, setTxnCarrierName] = useState([]);
  const [txnShipFrom, setTxnShipFrom] = useState([]);
  const [txnShipFromName, setTxnShipFromName] = useState([]);
  const [txnShipTo, setTxnShipTo] = useState([]);
  const [txnShipToName, setTxnShipToName] = useState([]);
  const [txnShipMode, setTxnShipMode] = useState([]);

  const [txnSupplierOptions, setTxnSupplierOptions] = useState([]);
  const [txnCustomerOptions, setTxnCustomerOptions] = useState([]);
  const [txnCarrierOptions, setTxnCarrierOptions] = useState([]);
  const [txnFromSiteOptions, setTxnFromSiteOptions] = useState([]);

  const [loadingTxnSupplier, setLoadingTxnSupplier] = useState(false);
  const [loadingTxnCustomer, setLoadingTxnCustomer] = useState(false);
  const [loadingTxnCarrier, setLoadingTxnCarrier] = useState(false);
  const [loadingTxnFromSite, setLoadingTxnFromSite] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let isCancelled = false;

    const loadRegions = async () => {
      setLoadingRegions(true);
      try {
        const regionList = await fetchRegions(userEmail);
        if (!isCancelled) {
          setRegions(normalizeOptions(regionList));
        }
      } catch (error) {
        if (!isCancelled) {
          setRegions([]);
          // eslint-disable-next-line no-console
          console.error("Failed to load regions", error);
        }
      } finally {
        if (!isCancelled) {
          setLoadingRegions(false);
        }
      }
    };

    loadRegions();

    return () => {
      isCancelled = true;
    };
  }, [open, userEmail]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCancelled = false;

    const loadPlants = async () => {
      setLoadingPlants(true);
      try {
        const response = await fetchPlants(userEmail, selectedRegions);
        if (isCancelled) {
          return;
        }

        const plantList = Array.isArray(response?.content?.plants)
          ? response.content.plants
          : Array.isArray(response)
            ? response
            : [];

        setPlants(normalizeOptions(plantList));
      } catch (error) {
        if (!isCancelled) {
          setPlants([]);
          // eslint-disable-next-line no-console
          console.error("Failed to load plants", error);
        }
      } finally {
        if (!isCancelled) {
          setLoadingPlants(false);
        }
      }
    };

    loadPlants();

    return () => {
      isCancelled = true;
    };
  }, [open, selectedRegions, userEmail]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCancelled = false;

    const loadMaterialTypes = async () => {
      setLoadingMaterialTypes(true);
      try {
        const response = await fetchMaterialTypes(selectedPlants);
        if (isCancelled) {
          return;
        }

        const typeList = Array.isArray(response?.content?.materialType)
          ? response.content.materialType
          : Array.isArray(response)
            ? response
            : [];

        setMaterialTypes(normalizeOptions(typeList));
      } catch (error) {
        if (!isCancelled) {
          setMaterialTypes([]);
          // eslint-disable-next-line no-console
          console.error("Failed to load material types", error);
        }
      } finally {
        if (!isCancelled) {
          setLoadingMaterialTypes(false);
        }
      }
    };

    loadMaterialTypes();

    return () => {
      isCancelled = true;
    };
  }, [open, selectedPlants]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCancelled = false;

    const loadMrpControllers = async () => {
      setLoadingMrpControllers(true);
      try {
        const response = await fetchMrpControllers(
          selectedPlants,
          selectedMaterialTypes
        );
        if (isCancelled) {
          return;
        }

        const controllerList = Array.isArray(response?.content?.mrpControllers)
          ? response.content.mrpControllers
          : Array.isArray(response)
            ? response
            : [];

        setMrpControllers(normalizeOptions(controllerList));
      } catch (error) {
        if (!isCancelled) {
          setMrpControllers([]);
          // eslint-disable-next-line no-console
          console.error("Failed to load MRP controllers", error);
        }
      } finally {
        if (!isCancelled) {
          setLoadingMrpControllers(false);
        }
      }
    };

    loadMrpControllers();

    return () => {
      isCancelled = true;
    };
  }, [open, selectedPlants, selectedMaterialTypes]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const hasValidSearch =
      debouncedMaterialSearch === "" || debouncedMaterialSearch.length >= 3;

    if (!hasValidSearch) {
      setMaterials([...selectedMaterials]);
      setLoadingMaterials(false);
      return;
    }

    let isCancelled = false;

    const loadMaterials = async () => {
      setLoadingMaterials(true);
      try {
        const response = await fetchMaterials(
          selectedPlants,
          selectedMaterialTypes,
          selectedMrpControllers.length > 0
            ? selectedMrpControllers
            : [],
          CATEGORY_CODE,
          {
            page: 0,
            size: MATERIAL_PAGE_SIZE,
            searchText: debouncedMaterialSearch,
          }
        );

        if (isCancelled) {
          return;
        }

        const materialList = Array.isArray(response?.content)
          ? response.content
          : Array.isArray(response)
            ? response
            : [];

        const mergedList = Array.from(
          new Set([...materialList, ...selectedMaterials])
        );

        setMaterials(normalizeOptions(mergedList));
      } catch (error) {
        if (!isCancelled) {
          setMaterials(selectedMaterials.length > 0 ? selectedMaterials : []);
          // eslint-disable-next-line no-console
          console.error("Failed to load materials", error);
        }
      } finally {
        if (!isCancelled) {
          setLoadingMaterials(false);
        }
      }
    };

    loadMaterials();

    return () => {
      isCancelled = true;
    };
  }, [
    open,
    selectedPlants,
    selectedMaterialTypes,
    selectedMrpControllers,
    selectedMaterials,
    debouncedMaterialSearch,
  ]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let isCancelled = false;

    const loadSavedFilters = async () => {
      setSavedFiltersLoading(true);
      try {
        const fetchedFilters = await fetchSavedFilters();
        if (!isCancelled) {
          setSavedFilters(fetchedFilters);
        }
      } catch (error) {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error("Failed to load saved filters", error);
        }
      } finally {
        if (!isCancelled) {
          setSavedFiltersLoading(false);
        }
      }
    };

    loadSavedFilters();

    return () => {
      isCancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setDraftShipmentTypes(buildShipmentState(shipmentTypes));
    }
  }, [open, shipmentTypes]);

 useEffect(() => {
    if (!open || !showTransactional) {
      return;
    }

    let isCancelled = false;

    const loadMergedOptions = async (column, setOptions, setLoading) => {
      setLoading(true);
      try {
        const [inbound, outbound] = await Promise.all([
          fetchInboundFilterOptions(column, {}),
          fetchOutboundFilterOptions(column, {}),
        ]);
        if (!isCancelled) {
          const normalized = mergeUnique(
            normalizeOptions(inbound),
            normalizeOptions(outbound)
          );
          setOptions(normalized);
        }
      } catch (error) {
        if (!isCancelled) {
          setOptions([]);
          // eslint-disable-next-line no-console
          console.error(
            `Failed to load transactional options for ${column}`,
            error
          );
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadMergedOptions("supplier", setTxnSupplierOptions, setLoadingTxnSupplier);
    loadMergedOptions("customer", setTxnCustomerOptions, setLoadingTxnCustomer);
    loadMergedOptions("carrier", setTxnCarrierOptions, setLoadingTxnCarrier);
    loadMergedOptions("fromSite", setTxnFromSiteOptions, setLoadingTxnFromSite);

    return () => {
      isCancelled = true;
    };
  }, [open, showTransactional]);

  const selectedFilter = useMemo(
    () => savedFilters.find((filter) => filter.id === selectedFilterId) || null,
    [savedFilters, selectedFilterId]
  );

  const applyCriteriaToState = (criteria = {}) => {
    setSearchItem(criteria.searchItem ?? "");
    setSearchSite(criteria.searchSite ?? "");
    setShipFrom(criteria.shipFrom ?? "");
    setShipTo(criteria.shipTo ?? "");
    setCarrier(criteria.carrier ?? "");
    setShipMode(criteria.shipMode ?? "");
    setDraftShipmentTypes(buildShipmentState(criteria.shipmentTypes));
    setSelectedRegions(Array.isArray(criteria.regions) ? criteria.regions : []);
    setSelectedPlants(Array.isArray(criteria.plants) ? criteria.plants : []);
    setSelectedMaterialTypes(
      Array.isArray(criteria.materialTypes) ? criteria.materialTypes : []
    );
    setSelectedMrpControllers(
      Array.isArray(criteria.mrpControllers) ? criteria.mrpControllers : []
    );
    setSelectedMaterials(
      Array.isArray(criteria.materials) ? criteria.materials : []
    );
    setSelectedUoms(Array.isArray(criteria.uoms) ? criteria.uoms : []);
    setMaterialSearchText(criteria.materialSearchText ?? "");

    setTxnLoads(Array.isArray(criteria.txnLoads) ? criteria.txnLoads : []);
    setTxnSupplierNumber(
      Array.isArray(criteria.txnSupplierNumber) ? criteria.txnSupplierNumber : []
    );
    setTxnSupplierName(
      Array.isArray(criteria.txnSupplierName) ? criteria.txnSupplierName : []
    );
    setTxnCustomerNumber(
      Array.isArray(criteria.txnCustomerNumber) ? criteria.txnCustomerNumber : []
    );
    setTxnCustomerName(
      Array.isArray(criteria.txnCustomerName) ? criteria.txnCustomerName : []
    );
    setTxnCarrierNumber(
      Array.isArray(criteria.txnCarrierNumber) ? criteria.txnCarrierNumber : []
    );
    setTxnCarrierName(
      Array.isArray(criteria.txnCarrierName) ? criteria.txnCarrierName : []
    );
    setTxnShipFrom(
      Array.isArray(criteria.txnShipFrom) ? criteria.txnShipFrom : []
    );
    setTxnShipFromName(
      Array.isArray(criteria.txnShipFromName) ? criteria.txnShipFromName : []
    );
    setTxnShipTo(Array.isArray(criteria.txnShipTo) ? criteria.txnShipTo : []);
    setTxnShipToName(
      Array.isArray(criteria.txnShipToName) ? criteria.txnShipToName : []
    );
    setTxnShipMode(
      Array.isArray(criteria.txnShipMode) ? criteria.txnShipMode : []
    );
  };

  const resetAll = () => {
    setSelectedFilterId(null);
    setFilterName("");
    setSaveError("");
    applyCriteriaToState({});
  };

  const handleSelectSavedFilter = (_, option) => {
    setSaveError("");

    if (!option) {
      setSelectedFilterId(null);
      setFilterName("");
      applyCriteriaToState({});
      return;
    }

    setSelectedFilterId(option.id);
    setFilterName(option.name ?? "");
    applyCriteriaToState(option.criteria || {});
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError("");

    const trimmedName = filterName.trim();
    const criteria = {
      searchItem,
      searchSite,
      shipFrom,
      shipTo,
      carrier,
      shipMode,
      shipmentTypes: draftShipmentTypes,
      regions: selectedRegions,
      plants: selectedPlants,
      materialTypes: selectedMaterialTypes,
      mrpControllers: selectedMrpControllers,
      materials: selectedMaterials,
      uoms: selectedUoms,
      materialSearchText,

      txnLoads,
      txnSupplierNumber,
      txnSupplierName,
      txnCustomerNumber,
      txnCustomerName,
      txnCarrierNumber,
      txnCarrierName,
      txnShipFrom,
      txnShipFromName,
      txnShipTo,
      txnShipToName,
      txnShipMode,
    };

    try {
      if (trimmedName) {
        const savedFilter = await upsertSavedFilter({
          id: selectedFilterId,
          name: trimmedName,
          criteria,
        });

        setSavedFilters((prev) => {
          const existingIndex = prev.findIndex(
            (entry) => entry.id === savedFilter.id
          );
          if (existingIndex >= 0) {
            const next = [...prev];
            next[existingIndex] = savedFilter;
            return next;
          }
          return [savedFilter, ...prev];
        });
        setSelectedFilterId(savedFilter.id);
      }

      setShipmentTypes({ ...draftShipmentTypes });
      onClose();
    } catch (error) {
      const message =
        error?.message || "Unable to save the filter configuration.";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Restrict Drawer scrolling to main content area
  const mainContent = typeof window !== 'undefined' ? document.querySelector('.main-content .content') : null;
  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      onClose={onClose}
      container={mainContent || undefined}
      ModalProps={{ disablePortal: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          position: 'absolute',
          left: SIDE_DRAWER_WIDTH,
          top: HEADER_HEIGHT,
          height: `calc(100% - ${HEADER_HEIGHT}px)`,
          zIndex: 10,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          maxHeight: 'calc(100vh - 84px)', // restrict to main content height
          scrollbarWidth: 'thin',
          scrollbarColor: '#b5b5b5 transparent',
          '&::-webkit-scrollbar': {
            width: '3px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: '8px',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.35)',
          },
        },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "#fff",
        }}
      >
        {/* Header with Close Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, py: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1A535C", fontSize: 18 }}>
              {title}
            </Typography>
            <Tooltip title={tooltip} arrow>
              <InfoOutlinedIcon sx={{ color: "#1A535C", fontSize: 18 }} />
            </Tooltip>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#1A535C" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 1, borderColor: "#e5e7eb" }} />

        <ToggleButtonGroup
          value={showTransactional ? 'transactional' : 'global'}
          exclusive
          onChange={(event, newValue) => {
            if (newValue !== null) setShowTransactional(newValue === 'transactional');
          }}
          sx={{ mb: 1.5 }}
        >
          <ToggleButton value="global" selected={!showTransactional} sx={{ textTransform: 'none', minWidth: 80, px: 1, py: 0.5, fontSize: 14, height: 32 }}>
            Global Filter
          </ToggleButton>
          <ToggleButton value="transactional" selected={showTransactional} sx={{ textTransform: 'none', minWidth: 100, px: 1, py: 0.5, fontSize: 12, height: 32 }}>
            Transactional Filter
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Scrollable Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Autocomplete
            size="small"
            value={selectedFilter}
            onChange={handleSelectSavedFilter}
            options={savedFilters}
            loading={savedFiltersLoading}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            clearOnBlur={false}
            noOptionsText={
              savedFiltersLoading
                ? "Loading saved filters..."
                : "No saved filters"
            }
            renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Saved Filters"
                    placeholder="Select a saved filter"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {savedFiltersLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                        "&:hover fieldset": { borderColor: "#1A535C" },
                        "&.Mui-focused fieldset": { borderColor: "#1A535C" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
            )}
          />

          <TextField
            label="Filter Name"
            placeholder="Enter filter name"
            size="small"
            value={filterName}
            onChange={(event) => {
              if (saveError) {
                setSaveError("");
              }
              setFilterName(event.target.value);
            }}
            sx={{
              fontSize: 13,
              "& .MuiOutlinedInput-root": {
                fontSize: 13,
                "&:hover fieldset": { borderColor: "#1A535C" },
                "&.Mui-focused fieldset": { borderColor: "#1A535C" },
              },
              "& .MuiInputLabel-root": {
                fontSize: 13,
              },
            }}
          />

          {!showTransactional ? (
            <>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={regions}
                  filterOptions={(options) => options}

                value={selectedRegions}
                loading={loadingRegions}
                onChange={(_, values) => setSelectedRegions(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Regions"
                    placeholder="Select regions"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingRegions ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={plants}
                  filterOptions={(options) => options}

                value={selectedPlants}
                loading={loadingPlants}
                onChange={(_, values) => setSelectedPlants(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plants"
                    placeholder="Select plants"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPlants ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={materialTypes}
                  filterOptions={(options) => options}
                value={selectedMaterialTypes}
                loading={loadingMaterialTypes}
                onChange={(_, values) => setSelectedMaterialTypes(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Material Types"
                    placeholder="Select material types"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMaterialTypes ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={UOM_OPTIONS}
                  filterOptions={(options) => options}
                value={selectedUoms}
                onChange={(_, values) => setSelectedUoms(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="UOM"
                    placeholder="Select UOM"
                    size="small"
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={mrpControllers}
                  filterOptions={(options) => options}
                value={selectedMrpControllers}
                loading={loadingMrpControllers}
                onChange={(_, values) => setSelectedMrpControllers(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="MRP Controllers"
                    placeholder="Select MRP controllers"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMrpControllers ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={materials}
                  filterOptions={filterOptionsSafe}
                value={selectedMaterials}
                loading={loadingMaterials}
                onChange={(_, values) => setSelectedMaterials(values)}
                inputValue={materialSearchText}
                onInputChange={(_, value, reason) => {
                  if (reason === "clear") {
                    setMaterialSearchText("");
                    return;
                  }
                  setMaterialSearchText(value);
                }}
                size="small"
                limitTags={2}
                filterSelectedOptions
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                noOptionsText={
                  materialSearchText.length > 0 && materialSearchText.length < 3
                    ? "Type atleast 3 characters"
                    : "No materials found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Materials"
                    placeholder="Type altleast 3 characters"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMaterials ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    FormHelperTextProps={{ sx: { mt: 0.5, ml: 0 } }}
                    helperText={
                      materialSearchText.length > 0 &&
                        materialSearchText.length < 3
                        ? "Enter at least 3 characters to search"
                        : ""
                    }
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />
            </>
          ) : (
            <>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#1A535C" }}
              >
                Transactional Filter
              </Typography>

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={TXN_LOADS_OPTIONS}
                  filterOptions={(options) => options}
                value={txnLoads}
                onChange={(_, values) => setTxnLoads(values)}
                size="small"
                limitTags={2}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={option} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Loads"
                    size="small"
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnSupplierOptions}
                  filterOptions={(options) => options}
                value={txnSupplierNumber}
                loading={loadingTxnSupplier}
                onChange={(_, values) => setTxnSupplierNumber(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier Number"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnSupplier ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnSupplierOptions}
                  filterOptions={(options) => options}
                value={txnSupplierName}
                loading={loadingTxnSupplier}
                onChange={(_, values) => setTxnSupplierName(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier Name"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnSupplier ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnCustomerOptions}
                  filterOptions={(options) => options}
                value={txnCustomerNumber}
                loading={loadingTxnCustomer}
                onChange={(_, values) => setTxnCustomerNumber(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer Number"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnCustomer ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnCustomerOptions}
                  filterOptions={(options) => options}
                value={txnCustomerName}
                loading={loadingTxnCustomer}
                onChange={(_, values) => setTxnCustomerName(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer Name"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnCustomer ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnCarrierOptions}
                  filterOptions={(options) => options}
                value={txnCarrierNumber}
                loading={loadingTxnCarrier}
                onChange={(_, values) => setTxnCarrierNumber(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Carrier Number"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnCarrier ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnCarrierOptions}
                  filterOptions={(options) => options}
                value={txnCarrierName}
                loading={loadingTxnCarrier}
                onChange={(_, values) => setTxnCarrierName(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Carrier Name"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnCarrier ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnFromSiteOptions}
                filterOptions={(options) => options}
                value={txnShipFrom}
                loading={loadingTxnFromSite}
                onChange={(_, values) => setTxnShipFrom(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ship From"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnFromSite ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={txnFromSiteOptions}
                filterOptions={(options) => options}
                value={txnShipFromName}
                loading={loadingTxnFromSite}
                onChange={(_, values) => setTxnShipFromName(values)}
                size="small"
                limitTags={2}
                getOptionLabel={getOptionLabelSafe}
                isOptionEqualToValue={isOptionEqualToValueSafe}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={getOptionLabelSafe(option)} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ship From Name"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTxnFromSite ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={TXN_SHIP_TO_OPTIONS}
                filterOptions={(options) => options}
                value={txnShipTo}
                onChange={(_, values) => setTxnShipTo(values)}
                size="small"
                limitTags={2}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={option} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ship To"
                    size="small"
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={TXN_SHIP_TO_NAME_OPTIONS}
                filterOptions={(options) => options}
                value={txnShipToName}
                onChange={(_, values) => setTxnShipToName(values)}
                size="small"
                limitTags={2}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={option} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ship To Name"
                    size="small"
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={TXN_SHIP_MODE_OPTIONS}
                  filterOptions={(options) => options}
                value={txnShipMode}
                onChange={(_, values) => setTxnShipMode(values)}
                size="small"
                limitTags={2}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={option} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ship Mode"
                    size="small"
                    sx={{
                      fontSize: 13,
                      "& .MuiOutlinedInput-root": {
                        fontSize: 13,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />
            </>
          )}
        </Box>



        {saveError ? (
          <Typography variant="caption" color="error" sx={{ mb: 2 }}>
            {saveError}
          </Typography>
        ) : null}

        {/* Footer Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.5, borderTop: '1px solid #e5e7eb', mt: 0.5 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={resetAll}
            sx={{
              minWidth: 56,
              height: 32,
              p: 0.25,
              borderColor: "#1A535C",
              color: "#1A535C",
              bgcolor: '#F7F9FA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(26,83,92,0.08)',
              '&:hover': { bgcolor: '#e3f2fd', borderColor: '#0F3B45', color: '#0F3B45' },
            }}
          >
            <RefreshIcon fontSize="small" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              minWidth: 56,
              height: 32,
              fontWeight: 600,
              p: 0.25,
              fontSize: 13,
              bgcolor: "#1A535C",
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(26,83,92,0.08)',
              '&:hover': { bgcolor: "#0F3B45" },
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            sx={{
              minWidth: 56,
              height: 32,
              fontWeight: 600,
              p: 0.25,
              fontSize: 13,
              borderColor: "#1A535C",
              color: "#1A535C",
              bgcolor: '#F7F9FA',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(26,83,92,0.08)',
              '&:hover': { bgcolor: '#e3f2fd', borderColor: '#0F3B45', color: '#0F3B45' },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default GlobalFilter;